<template>
    <div @mouseover="isHovered = true"
         @mouseleave="isHovered = false"
         class="position-relative templated-value-editor">
        <quill-editor
            :class="isHtml ? 'templated-value-editor-html': 'templated-value-editor-text'"
            ref="editor"
            v-model="content"
            @change="onValueChange"
            @ready="onQuillReady"
            :disabled="disabled"
            :options="quillOptions">
        </quill-editor>
        <transition name="app-dropdown-fade">
            <AppTemplatedValueEditorPlaceholderSelect
                v-show="isHovered"
                :objectsMap="objectsMap"
                @select="onObjectFieldPlaceholderAdd"
                class="add-placeholder"/>
        </transition>
    </div>
</template>

<script>
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import {quillEditor} from 'vue-quill-editor'
import {
    TEMPLATED_VALUE_PLACEHOLDER_DATA_PROP,
    TemplatedValueEditorPlaceholder
} from '@/components/templated-value/TemplatedValueEditorPlaceholder'
import Quill from 'quill'
import {
    defaultDelimiters,
    replaceHtmlTagsWithPlaceholders,
    replacePlaceholdersWithHtmlTags
} from '@/components/templated-value/index'
import {createHtmlElementFromString, wrapHtmlStringWithTag} from '@/utils/dom'
import AppTemplatedValueEditorPlaceholderSelect
    from '@/components/templated-value/AppTemplatedValueEditorPlaceholderSelect.vue'

const Delta = Quill.imports.delta

export default {
    name: 'AppTemplatedValueEditor',
    components: {
        AppTemplatedValueEditorPlaceholderSelect,
        quillEditor
    },
    props: {
        value: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Value'
        },
        isHtml: {
            type: Boolean,
            default: false
        },
        // if isHtml: true
        wrapHtmlPlaceholderInnerText: {
            type: Boolean,
            default: true
            // true: <div ...>{{ company.name }}</div>   (wrap in delimiters)
            // false: <div ...>Company name</div>        (as placeholder label)
        },
        objectsMap: {
            type: Object, // {[key: string]: string} - key: placeholder value, value: placeholder label
            default: () => {
            }
        },
        disabled: {
            type: Boolean,
            default: false
        },
        delimiters: {
            type: Array,
            default: () => defaultDelimiters
        }
    },
    model: {
        prop: 'value',
        event: 'change'
    },
    data() {
        return {
            content: '',
            cursor: 0,
            isHovered: false,
            isLoading: false
        }
    },
    computed: {
        quill() {
            return this.$refs.editor.quill
        },

        quillOptions() {
            if (this.isHtml) {
                return {
                    theme: 'snow',
                    placeholder: this.placeholder,
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline', 'strike'],
                            [{'list': 'ordered'}, {'list': 'bullet'}],
                            ['link']
                        ]
                    }
                }
            } else {
                return {
                    theme: 'snow',
                    placeholder: this.placeholder,
                    modules: {
                        toolbar: false
                    }
                }
            }
        }
    },
    methods: {
        onValueChange({html, text}) {
            if (this.isLoading) {
                return
            }
            this.moveCursor()
            let value = this.wrapHtmlString(html)
            const el = replaceHtmlTagsWithPlaceholders({
                htmlString: value,
                delimiters: this.delimiters
            })

            if (this.isHtml) {
                if (this.wrapHtmlPlaceholderInnerText) {
                    value = _.get(el, 'outerHTML', '')
                }
            } else {
                value = _.get(el, 'innerText', '')
            }
            this.$emit('change', value)
        },

        setupInitialValue() {
            let value = this.value
            if (this.isHtml) {
                value = this.unwrapHtmlString(value)
                if (this.wrapHtmlPlaceholderInnerText) {
                    value = replacePlaceholdersWithHtmlTags({
                        htmlString: value,
                        objectsMap: this.objectsMap,
                        delimiters: this.delimiters
                    })
                }
            } else {
                value = replacePlaceholdersWithHtmlTags({
                    htmlString: wrapHtmlStringWithTag(this.value, 'p'),
                    objectsMap: this.objectsMap,
                    delimiters: this.delimiters
                })
            }
            this.quill.root.innerHTML = value
        },

        moveCursor() {
            const range = this.quill.getSelection()
            if (range) {
                this.cursor = range.index
            }
        },

        onObjectFieldPlaceholderAdd(field) {
            this.insertObjectFieldPlaceholder(field)
            this.focusQuill()
        },

        insertObjectFieldPlaceholder(field) {
            this.quill.updateContents(
                new Delta()
                    .retain(this.cursor)
                    .insert({[TEMPLATED_VALUE_PLACEHOLDER_DATA_PROP]: field})
            )
            const notEditableContentLength = 1
            this.cursor += notEditableContentLength
            this.quill.setSelection(this.cursor, 0)
        },

        insertText(text) {
            this.quill.insertText(this.cursor, text)
            this.cursor += text.length
        },

        async onQuillReady() {
            this.isLoading = true
            this.setupInitialValue()
            this.quill.on('selection-change', range => {
                if (range && range.length === 0) {
                    this.cursor = range.index
                }
            })
            this.isLoading = false
        },

        focusQuill() {
            this.quill.setSelection(this.cursor, 0)
        },

        registerDocumentPropertyTagParchment() {
            Quill.register(TemplatedValueEditorPlaceholder)
        },

        /**
         * Extra wrapper is needed for the server to parse the html correctly.
         */
        wrapHtmlString(htmlString) {
            return wrapHtmlStringWithTag(htmlString, 'div')
        },

        /**
         * Removes extra wrapper; with it quill cannot insert value into editor
         */
        unwrapHtmlString(htmlString) {
            if (!htmlString) {
                return ''
            }
            const el = createHtmlElementFromString(htmlString)
            return el.innerHTML
        }
    },
    created() {
        this.registerDocumentPropertyTagParchment()
    }
}
</script>

<style lang="scss" scoped>
.add-placeholder {
    position: absolute;
    right: 6px;
    top: 5px;
    background: white;
}
</style>

<style lang="scss">
.templated-value-editor {
    .templated-value-editor-text {
        .ql-container {
            border-radius: 4px !important;
        }
    }

    .templated-value-editor-html {
        .ql-container {
            border-radius: 0 0 4px 4px;
        }

        .ql-toolbar {
            border-radius: 4px 4px 0 0;
        }
    }

    .ql-editor {
        padding: 7px 16px !important;
        line-height: 24px;
    }

    [data-document-property] {
        background-color: $color-purple-light;
        color: white;
        padding: 2px;
        border-radius: 4px;
        margin: 0 2px;
    }
}
</style>
